<!--
  黑金点餐 UI v2（菜单列表页）
  蓝湖：无商品选购页 / 折叠今日推荐页 均为本页不同状态
  业务逻辑复用 BlackMicropoint
-->
<template>
  <div class="bv2-page" @scroll.passive="handleGoodsScroll">
    <header class="bv2-hero">
      <div class="bv2-brand">
        <h1>{{ storeName }}</h1>
        <p>{{ storeSlogan }}</p>
        <button type="button" class="bv2-order-link" @click="openOrders">
          查看我的订单
          <img
            class="bv2-detail-arrow"
            src="@/assets/black-micropoint/v2-category-icons/right-arrow.svg"
            alt=""
          />
        </button>
      </div>

      <div class="bv2-banner-container" v-if="recommendGoodsList.length">
    <!-- 今日推荐：大图横幅（蓝湖 351×140 @750） -->
    <template>
        <van-swipe
          class="bv2-banner"
          :autoplay="recommendGoodsList.length > 1 ? 5000 : 0"
          :loop="recommendGoodsList.length > 1"
          :show-indicators="false"
          @change="handleRecommendChange"
        >
          <van-swipe-item
            v-for="goods in recommendGoodsList"
            :key="goods.Id || goods.GoodsCode"
          >
            <div class="bv2-banner-slide" @click="openDetail(goods)">
              <img
                :src="goods.PicUrl || placeholderImage"
                :alt="goods.GoodsName"
              />
              <div class="bv2-banner-mask">
                <div class="bv2-banner-copy">
                  <strong>今日推荐</strong>
                  <span>Today's Recommendation</span>
                </div>
                <button
                  type="button"
                  class="bv2-banner-detail-link"
                  @click.stop="openDetail(goods)"
                >
                  详情
                  <img
                    class="bv2-detail-arrow"
                    src="@/assets/black-micropoint/v2-category-icons/right-arrow.svg"
                    alt=""
                  />
                </button>
              </div>
            </div>
          </van-swipe-item>
        </van-swipe>
        <div
          v-if="recommendGoodsList.length > 1"
          class="bv2-dots"
          aria-hidden="true"
        >
          <i
            v-for="(goods, index) in recommendGoodsList"
            :key="`dot-${goods.Id || index}`"
            :class="{ active: recommendActiveIndex === index }"
          ></i>
        </div>
      </template>
      </div>
  
    </header>

    <div class="bv2-body">
      <div class="bv2-search">
        <div class="bv2-search-inner" @click="focusSearchInput">
          <div
            class="bv2-search-wrap"
            :class="{ 'is-active': searchInputActive }"
          >
            <div v-show="showSearchDecor" class="bv2-search-decor" aria-hidden="true">
              <img
                class="bv2-search-icon"
                src="@/assets/black-micropoint/icons/search-scan2.png"
                alt=""
              />
              <span>搜索你感兴趣的美食吧～</span>
            </div>
            <input
              ref="searchInput"
              v-model.trim="searchKeyword"
              type="text"
              inputmode="search"
              autocomplete="off"
              class="bv2-search-input"
              aria-label="搜索美食"
              @focus="handleSearchFocus"
              @blur="handleSearchBlur"
            />
            <button
              v-show="searchInputActive"
              type="button"
              class="bv2-search-clear-btn"
              aria-label="清空搜索"
              @mousedown.prevent.stop="clearSearchKeyword"
              @touchend.prevent.stop="clearSearchKeyword"
            >
              ×
            </button>
          </div>
        </div>
      </div>

      <main class="bv2-menu">
      <aside class="bv2-cats" ref="catsScroll">
        <button
          v-for="category in categoryList"
          :key="category.id"
          type="button"
          :data-cat-nav="category.id"
          :class="{ active: activeCategoryId === category.id }"
          @click="selectCategory(category.id)"
        >
          <img class="bv2-cat-icon" :src="category.icon" alt="" />
          <span>{{ category.name }}</span>
          <i v-if="category.count">{{ category.count }}</i>
        </button>
      </aside>

      <!-- 右侧连续滚动：各分类依次排布，滚动联动左侧选中 -->
      <section class="bv2-goods">
        <div v-if="loading" class="bv2-state">菜品加载中...</div>
        <div v-else-if="!scrollCategorySections.length" class="bv2-state">
          暂无相关菜品
        </div>
        <template v-else>
          <div
            v-for="section in scrollCategorySections"
            :key="section.id"
            class="bv2-goods-section"
            :data-category-id="section.id"
          >
            <div class="bv2-cat-title">
              <strong>{{ section.name }}</strong>
            </div>
            <article
              v-for="goods in section.goods"
              :key="goods.Id"
              class="bv2-card"
              :class="{ 'is-soldout': isFullySoldOut(goods) }"
            >
              <div class="bv2-card-media" @click="openDetail(goods)">
                <div class="bv2-card-media-clip">
                  <img
                    :src="goods.PicUrl || placeholderImage"
                    :alt="goods.GoodsName"
                  />
                  <div
                    v-if="isFullySoldOut(goods)"
                    class="bv2-soldout-mask"
                    aria-hidden="true"
                  ></div>
                  <div
                    v-if="isFullySoldOut(goods)"
                    class="bv2-soldout-corner"
                    aria-hidden="true"
                  >
                    <span class="bv2-soldout-tag">已售罄</span>
                  </div>
                  <button
                    type="button"
                    class="bv2-detail-link"
                    @click.stop="openDetail(goods)"
                  >
                    详情
                    <img
                      class="bv2-detail-arrow"
                      src="@/assets/black-micropoint/v2-category-icons/right-arrow.svg"
                      alt=""
                    />
                  </button>
                </div>
                <span v-if="goods.IsRecommend" class="bv2-tag">招牌菜</span>
              </div>
              <div class="bv2-card-info">
                <strong class="bv2-card-name">{{ goods.GoodsName }}</strong>
                <div class="bv2-card-right" @click.stop>
                  <p class="bv2-card-price">
                    ¥{{ formatMoney(getGoodsPrice(goods))
                    }}<small>/{{ goods.Unit || "份" }}</small>
                  </p>
                  <template v-if="!isFullySoldOut(goods)">
                    <button
                      v-if="isSetMeal(goods)"
                      type="button"
                      class="bv2-spec-btn"
                      @click="openSetMeal(goods)"
                    >
                      选套餐
                    </button>
                    <button
                      v-else-if="needsSpecification(goods)"
                      type="button"
                      class="bv2-spec-btn"
                      @click="openSpecification(goods)"
                    >
                      选规格
                    </button>
                    <div
                      v-else-if="getSimpleGoodsQuantity(goods)"
                      class="bv2-qty"
                    >
                      <button
                        type="button"
                        @click="changeSimpleGoodsQuantity(goods, -1)"
                      >
                        −
                      </button>
                      <span>{{ getSimpleGoodsQuantity(goods) }}</span>
                      <button
                        type="button"
                        @click="changeSimpleGoodsQuantity(goods, 1)"
                      >
                        ＋
                      </button>
                    </div>
                    <button
                      v-else
                      type="button"
                      class="bv2-add-btn"
                      @click="addSimpleGoods(goods)"
                    >
                      ＋
                    </button>
                  </template>
                </div>
              </div>
            </article>
          </div>
        </template>
      </section>
      </main>
    </div>

    <footer class="bv2-cart">
      <div class="bv2-cart-container">
      <div class="bv2-cart-left" @click="openCart">
        <div class="bv2-cart-icon">
          <img src="@/assets/black-micropoint/v2-cart-bowl.svg" alt="" />
          <i v-if="cartTotalCount">{{ cartTotalCount }}</i>
        </div>
        <div class="bv2-cart-summary">
          <span v-if="!cartTotalCount">空空如也，快来选购吧</span>
          <template v-else>
            <strong>¥{{ formatMoney(cartTotalMoney) }}</strong>
            <em>共 {{ cartTotalCount }} 份</em>
          </template>
        </div>
      </div>
      <button
        type="button"
        class="bv2-submit"
        :disabled="isSubmitting"
        @click.stop="handleSubmit"
      >
        去下单
      </button>
    </div>
    </footer>

    <!-- 复用原黑金弹窗结构，样式在本页覆盖 -->
    <van-popup
      v-model="showDetailPopup"
      class="bv2-popup"
      position="bottom"
      round
      :get-container="getPopupContainer"
      @closed="resetDetailPopup"
    >
      <section v-if="detailGoods" class="bv2-detail">
        <header>
          <div>
            <strong>{{ detailGoods.GoodsName || "今日推荐" }}</strong>
            <span>{{ detailGoods.Description || "甄选当季食材，匠心烹制" }}</span>
          </div>
          <button type="button" @click="closeDetail" class="bv2-close-btn">×</button>
        </header>
        <img
          :src="detailGoods.PicUrl || placeholderImage"
          :alt="detailGoods.GoodsName || '今日推荐'"
        />
        <div class="bv2-detail-body">
          <p>{{ detailGoods.Detail || detailGoods.Description || "无更多介绍" }}</p>
          <div
            v-if="needsSpecification(detailGoods)"
            class="bv2-detail-spec-body"
          >
            <div class="bv2-spec-group">
              <h3 class="opacity-50">份量</h3>
              <div class="bv2-chips">
                <button
                  v-for="spec in detailSpecificationOptions"
                  :key="spec.id"
                  type="button"
                  :class="{ active: detailSelectedSpecificationId === spec.id }"
                  @click="detailSelectedSpecificationId = spec.id"
                >
                  {{ spec.name }}
                </button>
              </div>
            </div>
            <div v-if="detailRemarkOptions.length" class="bv2-spec-group">
              <h3 class="opacity-50">忌口</h3>
              <div class="bv2-chips">
                <button
                  v-for="remark in detailRemarkOptions"
                  :key="remark"
                  type="button"
                  :class="{ active: detailSelectedRemarks.includes(remark) }"
                  @click="toggleDetailRemark(remark)"
                >
                  {{ remark }}
                </button>
              </div>
            </div>
            <div class="bv2-spec-group">
              <h3 class="opacity-50">备注 (非必选) ：</h3>
              <textarea
                v-model.trim="detailCustomRemark"
                maxlength="80"
                placeholder="如有过敏源请备注"
              ></textarea>
            </div>
          </div>
          <div
            v-else-if="getDetailRemarkOptions(detailGoods).length"
            class="bv2-detail-remark"
          >
            <h3 class="opacity-50">备注：</h3>
            <p>{{ getDetailRemarkOptions(detailGoods).join("、") }}</p>
          </div>
          <div class="bv2-detail-price pop-detail-price">
            <strong>¥{{
                formatMoney(
                  needsSpecification(detailGoods)
                    ? detailSelectedSpecificationPrice
                    : getGoodsPrice(detailGoods)
                )
              }}<small>/{{ detailGoods.Unit || "份" }}</small>
            </strong>
            <button
              v-if="isSetMeal(detailGoods)"
              type="button"
              class="bv2-spec-btn"
              @click="addDetailGoods"
            >
              选套餐
            </button>
            <div v-else class="bv2-qty">
              <button
                type="button"
                :disabled="detailQuantity <= detailMinimumQuantity"
                @click="changeDetailQuantity(-1)"
              >
                −
              </button>
              <span>{{ detailQuantity }}</span>
              <button
                type="button"
                :disabled="detailQuantity >= 99"
                @click="changeDetailQuantity(1)"
              >
                ＋
              </button>
            </div>
          </div>
        </div>
        <footer v-if="!isSetMeal(detailGoods)" class="bv2-detail-footer-box">
          <button
            type="button"
            class="bv2-spec-btn block bv2-submit-btn"
            :disabled="
              needsSpecification(detailGoods) && !detailSelectedSpecificationId
            "
            @click="addDetailGoods"
          >
            {{
              needsSpecification(detailGoods)
                ? "选好了，加入购物车"
                : "加入购物车"
            }}
          </button>
        </footer>
      </section>
    </van-popup>

    <van-popup
      v-model="showCartPopup"
      class="bv2-popup"
      position="bottom"
      round
      :get-container="getPopupContainer"
      @closed="resetCartPopup"
    >
      <section class="bv2-cart-popup">
        <header>
          <strong>已选菜品</strong>
          <button
            type="button"
            :disabled="!cartItems.length"
            @click="requestClearCart"
          >
            一键清空
          </button>
        </header>
        <div class="bv2-cart-list">
          <div v-if="!cartItems.length" class="bv2-state">购物车还是空的</div>
          <div
            v-for="item in cartItems"
            :key="item.key"
            class="bv2-cart-item"
          >
            <img :src="item.image || placeholderImage" alt="" />
            <div>
              <strong>{{ item.name }}</strong>
              <p v-if="item.remarkText" class="bv2-cart-remark">
                备注：{{ item.remarkText }}
              </p>
              <button
                v-if="item.isSetMeal"
                type="button"
                class="bv2-cart-edit"
                @click="openSetMeal(item.sourceGoods, item)"
              >
                修改套餐
              </button>
              <p>¥{{ formatMoney(item.price) }}</p>
            </div>
            <div class="bv2-qty">
              <button type="button" @click="changeCartQuantity(item.key, -1)">
                −
              </button>
              <span>{{ item.quantity }}</span>
              <button type="button" @click="changeCartQuantity(item.key, 1)">
                ＋
              </button>
            </div>
          </div>
        </div>
        <div class="bv2-cart-total">
          <span>共 {{ cartTotalCount }} 份</span>
          <strong>合计 ¥{{ formatMoney(cartTotalMoney) }}</strong>
        </div>
        <footer>
          <button type="button" class="ghost" @click="closeCart">关闭</button>
          <button
            type="button"
            class="bv2-spec-btn"
            :disabled="!cartItems.length || isSubmitting"
            @click="handleSubmit"
          >
            去提交
          </button>
        </footer>
      </section>
    </van-popup>

    <van-popup
      v-model="showClearCartConfirm"
      class="bv2-confirm"
      position="center"
      :overlay-style="{ backgroundColor: 'rgba(0, 0, 0, 0.76)' }"
      :close-on-click-overlay="false"
      :get-container="getPopupContainer"
      round
    >
      <section>
        <strong>清空购物车</strong>
        <p>确定要清空全部已选菜品吗？</p>
        <div>
          <button type="button" class="ghost" @click="cancelClearCart">
            取消
          </button>
          <button type="button" class="bv2-confirm-btn" @click="confirmClearCart">
            确定清空
          </button>
        </div>
      </section>
    </van-popup>

    <van-popup
      v-model="showSpecificationPopup"
      class="bv2-popup"
      position="bottom"
      round
      :get-container="getPopupContainer"
      @closed="resetSpecificationPopup"
    >
      <section v-if="editingGoods" class="bv2-spec">
        <header>
          <strong>{{ editingGoods.GoodsName }}</strong>
          <button type="button" @click="closeSpecification" class="bv2-close-btn">×</button>
        </header>
        <div class="bv2-spec-body">
          <div class="bv2-spec-group">
            <h3 class="opacity-50">份量</h3>
            <div class="bv2-chips">
              <button
                v-for="spec in specificationOptions"
                :key="spec.id"
                type="button"
                :class="{ active: selectedSpecificationId === spec.id }"
                @click="selectedSpecificationId = spec.id"
              >
                {{ spec.name }}
              </button>
            </div>
          </div>
          <div v-if="remarkOptions.length" class="bv2-spec-group">
            <h3 class="opacity-50">忌口</h3>
            <div class="bv2-chips">
              <button
                v-for="remark in remarkOptions"
                :key="remark"
                type="button"
                :class="{ active: selectedRemarks.includes(remark) }"
                @click="toggleRemark(remark)"
              >
                {{ remark }}
              </button>
            </div>
          </div>
          <div class="bv2-spec-group">
            <h3 class="opacity-50">备注 (非必选) ：</h3>
            <textarea
              v-model.trim="customRemark"
              maxlength="80"
              placeholder="如有过敏源请备注"
            ></textarea>
          </div>
        </div>
        <footer class="bv2-spec-footer">
          <div class="bv2-spec-price">
            <strong>¥{{ formatMoney(selectedSpecificationPrice) }}</strong>
            <small>/{{ editingGoods.Unit || "份" }}</small>
          </div>
          <button
            type="button"
            class="bv2-spec-submit"
            :disabled="!selectedSpecificationId"
            @click="confirmSpecification"
          >
            选好了，加入购物车
          </button>
        </footer>
      </section>
    </van-popup>

    <van-popup
      v-model="showSetMealPopup"
      class="bv2-popup"
      position="bottom"
      round
      :get-container="getPopupContainer"
      @closed="resetSetMealPopup"
    >
      <section class="bv2-setmeal">
        <header>
          <strong>{{
            setMealInfo.GoodsName ||
              (editingSetMealGoods && editingSetMealGoods.GoodsName)
          }}</strong>
          <button type="button" @click="closeSetMeal">×</button>
        </header>
        <div v-if="setMealLoading" class="bv2-state">套餐加载中...</div>
        <div v-else-if="!setMealGroups.length" class="bv2-state">
          暂无可选套餐内容
        </div>
        <div v-else class="bv2-setmeal-body">
          <section
            v-for="group in setMealGroups"
            :key="group.id"
            class="bv2-setmeal-group"
          >
            <div
              v-for="item in group.items"
              :key="item.slotKey"
              class="bv2-setmeal-item"
              :class="{ active: item.selected }"
            >
              <img
                :src="item.PicUrl || placeholderImage"
                :alt="item.GoodsName"
              />
              <span>
                <strong>{{ item.GoodsName }}</strong>
                <small>{{ item.Description || item.Unit || "套餐菜品" }}</small>
              </span>
              <button
                v-if="item.selected && hasSetMealChangeItems(item)"
                type="button"
                @click="openSetMealChange(item)"
              >
                更换菜品
              </button>
            </div>
          </section>
        </div>
        <footer>
          <strong>¥{{ formatMoney(getGoodsPrice(editingSetMealGoods)) }}</strong>
          <div class="bv2-qty">
            <button
              type="button"
              :disabled="setMealQuantity <= setMealMinimumQuantity"
              @click="changeSetMealQuantity(-1)"
            >
              −
            </button>
            <span>{{ setMealQuantity }}</span>
            <button
              type="button"
              :disabled="setMealQuantity >= 99"
              @click="changeSetMealQuantity(1)"
            >
              ＋
            </button>
          </div>
          <button
            type="button"
            class="bv2-spec-btn"
            :disabled="setMealLoading || !setMealGroups.length"
            @click="confirmSetMeal"
          >
            {{ editingSetMealCartKey ? "保存修改" : "加入购物车" }}
          </button>
        </footer>
      </section>
    </van-popup>

    <van-popup
      v-model="showSetMealChangePopup"
      class="bv2-popup"
      position="bottom"
      round
      :get-container="getPopupContainer"
      @closed="resetSetMealChangePopup"
    >
      <section class="bv2-setmeal-change">
        <header>
          <div>
            <strong>更换菜品</strong>
            <span>{{
              changingSetMealItem ? changingSetMealItem.GoodsName : ""
            }}</span>
          </div>
          <button type="button" @click="closeSetMealChange" class="bv2-close-btn">×</button>
        </header>
        <div class="bv2-setmeal-change-list">
          <button
            v-for="item in setMealChangeOptions"
            :key="item.id"
            type="button"
            :class="{ active: selectedSetMealChangeId === item.id }"
            @click="selectSetMealChange(item)"
          >
            <img
              :src="item.PicUrl || placeholderImage"
              :alt="item.GoodsName"
            />
            <span>
              <strong>{{ item.GoodsName }}</strong>
              <small>{{ item.Description || "可更换套餐菜品" }}</small>
            </span>
            <em v-if="item.Price">¥{{ formatMoney(item.Price) }}</em>
          </button>
        </div>
        <footer>
          <button type="button" class="ghost" @click="closeSetMealChange">
            取消
          </button>
          <button
            type="button"
            class="bv2-spec-btn"
            :disabled="!selectedSetMealChangeId"
            @click="confirmSetMealChange"
          >
            确认更换
          </button>
        </footer>
      </section>
    </van-popup>
  </div>
</template>

<script>
import BlackMicropoint from "../BlackMicropoint.vue";
import {
  BLACK_GOLDEN_UI_VERSION_KEY,
  getBlackCurrentConsumeRouteName,
  getBlackRecordConsumeRouteName,
  handleSessionStorage,
  setDevMockBlackGoldenUIVersion,
} from "@/utils";

const V2_DEFAULT_CATEGORY_ICON = require("@/assets/black-micropoint/v2-category-icons/default.svg");
/** 蓝湖 Frame 页切图：分类名精确匹配 */
const V2_CATEGORY_ICON_BY_NAME = {
  新品小龙虾: require("@/assets/black-micropoint/v2-category-icons/lobster.svg"),
  新品蓝龙虾: require("@/assets/black-micropoint/v2-category-icons/lobster-blue.svg"),
  超值套餐: require("@/assets/black-micropoint/v2-category-icons/setmeal.svg"),
  世界杯套餐: require("@/assets/black-micropoint/v2-category-icons/world-cup.svg"),
  雅韵冷碟: require("@/assets/black-micropoint/v2-category-icons/cold-dish.svg"),
  粤式烧腊: require("@/assets/black-micropoint/v2-category-icons/roast.svg"),
  匠心热肴: require("@/assets/black-micropoint/v2-category-icons/hot-dish.svg"),
  臻品位汤: require("@/assets/black-micropoint/v2-category-icons/soup.svg"),
  暖心主食: require("@/assets/black-micropoint/v2-category-icons/staple-food.svg"),
  调配酒系列: require("@/assets/black-micropoint/v2-category-icons/cocktail.svg"),
  酒水: require("@/assets/black-micropoint/v2-category-icons/cocktail.svg"),
  餐厅娱乐: require("@/assets/black-micropoint/v2-category-icons/entertainment.svg"),
  洋酒系列: require("@/assets/black-micropoint/v2-category-icons/liquor.svg"),
  烟: require("@/assets/black-micropoint/v2-category-icons/cigarette.svg"),
};
/** 蓝湖 Frame 页切图：关键词兜底匹配 */
const V2_CATEGORY_ICON_RULES = [
  {
    keywords: ["蓝龙虾"],
    icon: require("@/assets/black-micropoint/v2-category-icons/lobster-blue.svg"),
  },
  {
    keywords: ["小龙虾", "龙虾", "口味虾", "虾"],
    icon: require("@/assets/black-micropoint/v2-category-icons/lobster.svg"),
  },
  {
    keywords: ["世界杯"],
    icon: require("@/assets/black-micropoint/v2-category-icons/world-cup.svg"),
  },
  {
    keywords: ["套餐", "组合", "超值餐", "双人餐", "多人餐", "一人餐"],
    icon: require("@/assets/black-micropoint/v2-category-icons/setmeal.svg"),
  },
  {
    keywords: ["冷碟", "凉菜", "冷菜", "凉碟", "拼盘", "雅韵"],
    icon: require("@/assets/black-micropoint/v2-category-icons/cold-dish.svg"),
  },
  {
    keywords: ["烧腊", "烧烤", "烤"],
    icon: require("@/assets/black-micropoint/v2-category-icons/roast.svg"),
  },
  {
    keywords: ["热肴", "热菜", "炒菜", "招牌菜", "小炒", "炒", "干锅", "锅"],
    icon: require("@/assets/black-micropoint/v2-category-icons/hot-dish.svg"),
  },
  {
    keywords: ["位汤", "汤", "粥", "羹"],
    icon: require("@/assets/black-micropoint/v2-category-icons/soup.svg"),
  },
  {
    keywords: ["主食", "米饭", "面食", "面条", "粉", "饭", "米"],
    icon: require("@/assets/black-micropoint/v2-category-icons/staple-food.svg"),
  },
  {
    keywords: ["酒水", "调配酒", "鸡尾酒", "啤酒", "红酒", "白酒", "饮品", "饮料", "茶饮", "咖啡"],
    icon: require("@/assets/black-micropoint/v2-category-icons/cocktail.svg"),
  },
  {
    keywords: ["洋酒", "威士忌", "伏特加", "白兰地"],
    icon: require("@/assets/black-micropoint/v2-category-icons/liquor.svg"),
  },
  {
    keywords: ["娱乐", "扑克", "棋牌"],
    icon: require("@/assets/black-micropoint/v2-category-icons/entertainment.svg"),
  },
  {
    keywords: ["烟"],
    icon: require("@/assets/black-micropoint/v2-category-icons/cigarette.svg"),
  },
  {
    keywords: ["海鲜", "水产", "鱼", "蟹", "贝", "螺"],
    icon: require("@/assets/black-micropoint/v2-category-icons/lobster.svg"),
  },
  {
    keywords: ["水果", "果盘", "甜品", "甜点", "蛋糕", "小吃"],
    icon: require("@/assets/black-micropoint/v2-category-icons/cold-dish.svg"),
  },
];

export default {
  name: "BlackMicropointV2",
  extends: BlackMicropoint,
  data() {
    return {
      /** 点击左侧分类滚动时，短暂锁定右侧滚动联动，避免选中跳动 */
      categoryClickLock: false,
      categoryClickTimer: null,
      /** 详情弹窗（有规格菜品）选中的规格与备注 */
      detailSelectedSpecificationId: null,
      detailSelectedRemarks: [],
      detailCustomRemark: "",
      searchFocused: false,
    };
  },
  computed: {
    showSearchDecor() {
      return !this.searchKeyword && !this.searchFocused;
    },
    searchInputActive() {
      return this.searchFocused || !!this.searchKeyword;
    },
    /** V2 侧栏分类：使用蓝湖金渐变切图图标 */
    categoryList() {
      return this.categoryGroups.map((group) => ({
        id: group.GoodsCatalogId,
        name: group.GoodsCatalogName,
        icon: this.getCategoryIconV2(group.GoodsCatalogName),
        count: this.cartItems
          .filter((item) => item.catalogId === group.GoodsCatalogId)
          .reduce((total, item) => total + item.quantity, 0),
      }));
    },
    /**
     * 右侧连续滚动的分类区块（含搜索过滤）。
     * 滚动完一个分类后自然接上下一个分类菜品。
     */
    scrollCategorySections() {
      const keyword = String(this.searchKeyword || "").trim().toLowerCase();
      return (this.categoryGroups || [])
        .map((group) => {
          const goods = (group.Goods || []).filter(
            (item) =>
              !keyword ||
              String(item.GoodsName || "")
                .toLowerCase()
                .includes(keyword)
          );
          return {
            id: group.GoodsCatalogId,
            name: group.GoodsCatalogName,
            goods,
          };
        })
        .filter((section) => section.goods.length);
    },
    /** 详情弹窗内可选规格列表 */
    detailSpecificationOptions() {
      if (!this.detailGoods) return [];
      const specifications = this.detailGoods.Specifications || [];
      if (specifications.length) {
        return specifications.map((item) => ({
          id: String(item.Id),
          name: item.Name || item.SpecificName || "标准份",
          price: this.normalizePrice(
            item.Price != null ? item.Price : this.detailGoods.Price
          ),
        }));
      }
      return [
        {
          id: "default",
          name: "标准份",
          price: this.normalizePrice(this.detailGoods.Price),
        },
      ];
    },
    /** 详情弹窗内可选备注（口味 / 做法 / 预设备注） */
    detailRemarkOptions() {
      if (!this.detailGoods) return [];
      return [
        ...(this.detailGoods.TasteArray || []),
        ...(this.detailGoods.MethodArray || []),
        ...(this.detailGoods.GoodsRemarkArray || []),
      ]
        .map((item) => String(item || "").trim())
        .filter((item, index, array) => item && array.indexOf(item) === index);
    },
    /** 详情弹窗当前选中规格单价 */
    detailSelectedSpecificationPrice() {
      const specification = this.detailSpecificationOptions.find(
        (item) => item.id === this.detailSelectedSpecificationId
      );
      return specification ? specification.price : 0;
    },
  },
  watch: {
    loading(val) {
      if (!val) this.syncStickyOffsets();
    },
  },
  created() {
    // 本页即 Figma v2，强制写入版本，避免被父组件解析回 v1
    this.blackUiVersion = 2;
    handleSessionStorage.set(BLACK_GOLDEN_UI_VERSION_KEY, 2);
    if (process.env.NODE_ENV === "development") {
      setDevMockBlackGoldenUIVersion(2);
    }
  },
  mounted() {
    this.syncStickyOffsets();
    window.addEventListener("resize", this.syncStickyOffsets, { passive: true });
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.syncStickyOffsets);
    if (this.categoryClickTimer) {
      clearTimeout(this.categoryClickTimer);
      this.categoryClickTimer = null;
    }
  },
  methods: {
    /**
     * V2 顶栏与 header-bg 同色，并让背景延伸到 safe-area（状态栏）区域。
     */
    applyPageChromeTheme() {
      const headerBg = "#0a0a0a";
      const themeColorMeta = this.getOrCreateMeta("name", "theme-color");
      const statusBarMeta = this.getOrCreateMeta(
        "name",
        "apple-mobile-web-app-status-bar-style"
      );
      const colorSchemeMeta = this.getOrCreateMeta("name", "color-scheme");
      const viewportMeta = document.querySelector('meta[name="viewport"]');
      const appEl = document.getElementById("app");

      this.previousPageChromeTheme = {
        themeColor: themeColorMeta.getAttribute("content") || "",
        statusBar: statusBarMeta.getAttribute("content") || "",
        colorScheme: colorSchemeMeta.getAttribute("content") || "",
        htmlBackground: document.documentElement.style.backgroundColor,
        bodyBackground: document.body.style.backgroundColor,
        appBackground: appEl ? appEl.style.backgroundColor : "",
        viewport: viewportMeta ? viewportMeta.getAttribute("content") || "" : "",
      };

      themeColorMeta.setAttribute("content", headerBg);
      statusBarMeta.setAttribute("content", "black-translucent");
      colorSchemeMeta.setAttribute("content", "dark");
      document.documentElement.style.backgroundColor = headerBg;
      document.body.style.backgroundColor = headerBg;
      if (appEl) appEl.style.backgroundColor = headerBg;

      if (viewportMeta) {
        const content = viewportMeta.getAttribute("content") || "";
        if (!content.includes("viewport-fit=cover")) {
          viewportMeta.setAttribute(
            "content",
            `${content},viewport-fit=cover`.replace(/^,/, "")
          );
        }
      }
    },
    restorePageChromeTheme() {
      if (!this.previousPageChromeTheme) return;

      this.getOrCreateMeta("name", "theme-color").setAttribute(
        "content",
        this.previousPageChromeTheme.themeColor
      );
      this.getOrCreateMeta(
        "name",
        "apple-mobile-web-app-status-bar-style"
      ).setAttribute("content", this.previousPageChromeTheme.statusBar);
      this.getOrCreateMeta("name", "color-scheme").setAttribute(
        "content",
        this.previousPageChromeTheme.colorScheme
      );
      document.documentElement.style.backgroundColor =
        this.previousPageChromeTheme.htmlBackground;
      document.body.style.backgroundColor =
        this.previousPageChromeTheme.bodyBackground;

      const appEl = document.getElementById("app");
      if (appEl) {
        appEl.style.backgroundColor =
          this.previousPageChromeTheme.appBackground || "";
      }

      const viewportMeta = document.querySelector('meta[name="viewport"]');
      if (viewportMeta && this.previousPageChromeTheme.viewport != null) {
        viewportMeta.setAttribute("content", this.previousPageChromeTheme.viewport);
      }
    },
    syncStickyOffsets() {
      this.$nextTick(() => {
        const page = this.$el;
        const search = page && page.querySelector(".bv2-search");
        if (page && search) {
          page.style.setProperty("--bv2-search-h", `${search.offsetHeight}px`);
        }
      });
    },
    getStickyHeaderHeight() {
      const page = this.$el;
      if (page) {
        const v = getComputedStyle(page).getPropertyValue("--bv2-search-h");
        const parsed = parseFloat(v);
        if (parsed) return parsed;
      }
      const search = page && page.querySelector(".bv2-search");
      return search ? search.offsetHeight : 92;
    },
    /** 弹层挂在本页节点内，保证 scoped 样式生效 */
    getPopupContainer() {
      return this.$el;
    },
    /** 覆盖父级：本页始终为 v2 */
    resolveUiVersion() {
      this.blackUiVersion = 2;
      handleSessionStorage.set(BLACK_GOLDEN_UI_VERSION_KEY, 2);
    },
    /** V2 分类图标（蓝湖 Frame 切图） */
    getCategoryIconV2(categoryName) {
      const normalizedName = String(categoryName || "").trim();
      if (V2_CATEGORY_ICON_BY_NAME[normalizedName]) {
        return V2_CATEGORY_ICON_BY_NAME[normalizedName];
      }
      const matchedRule = V2_CATEGORY_ICON_RULES.find((rule) =>
        rule.keywords.some((keyword) => normalizedName.includes(keyword))
      );
      return matchedRule ? matchedRule.icon : V2_DEFAULT_CATEGORY_ICON;
    },
    /**
     * 点击左侧分类：选中并滚动右侧到对应分类标题。
     */
    selectCategory(categoryId) {
      this.searchKeyword = "";
      this.searchFocused = false;
      this.activeCategoryId = categoryId;
      this.categoryClickLock = true;
      this.$nextTick(() => {
        const page = this.$el;
        if (!page) {
          this.categoryClickLock = false;
          return;
        }
        const target = page.querySelector(`[data-category-id="${categoryId}"]`);
        if (target) {
          const stickyTop = this.getStickyHeaderHeight();
          const pageRect = page.getBoundingClientRect();
          const targetRect = target.getBoundingClientRect();
          page.scrollTop =
            page.scrollTop + targetRect.top - pageRect.top - stickyTop;
        }
        this.scrollActiveCategoryIntoView();
        if (this.categoryClickTimer) clearTimeout(this.categoryClickTimer);
        this.categoryClickTimer = setTimeout(() => {
          this.categoryClickLock = false;
          this.categoryClickTimer = null;
        }, 360);
      });
    },
    /**
     * 页面滚动时，根据当前可视分类同步左侧选中态。
     */
    handleGoodsScroll() {
      if (this.categoryClickLock) return;
      const page = this.$el;
      if (!page) return;
      const sections = page.querySelectorAll("[data-category-id]");
      if (!sections.length) return;

      const threshold = this.getStickyHeaderHeight() + 8;
      let currentId = null;
      sections.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top <= threshold) {
          currentId = el.getAttribute("data-category-id");
        }
      });
      if (currentId == null) {
        currentId = sections[0].getAttribute("data-category-id");
      }

      const matched = this.categoryList.find(
        (item) => String(item.id) === String(currentId)
      );
      if (matched && matched.id !== this.activeCategoryId) {
        this.activeCategoryId = matched.id;
        this.scrollActiveCategoryIntoView();
      }
    },
    /** 左侧导航滚到当前选中项，避免高亮项跑出可视区 */
    scrollActiveCategoryIntoView() {
      this.$nextTick(() => {
        const nav = this.$refs.catsScroll;
        if (!nav) return;
        const btn = nav.querySelector(
          `[data-cat-nav="${this.activeCategoryId}"]`
        );
        if (!btn) return;
        const navRect = nav.getBoundingClientRect();
        const btnRect = btn.getBoundingClientRect();
        if (btnRect.top < navRect.top || btnRect.bottom > navRect.bottom) {
          nav.scrollTop +=
            btnRect.top - navRect.top - navRect.height / 2 + btnRect.height / 2;
        }
      });
    },
    openOrders() {
      this.$router.push({ name: getBlackRecordConsumeRouteName() });
    },
    /** 打开详情时初始化规格 / 备注选择状态 */
    openDetail(goods) {
      BlackMicropoint.methods.openDetail.call(this, goods);
      this.initDetailSpecificationState(goods);
    },
    /** 详情关闭后清理规格 / 备注选择状态 */
    resetDetailPopup() {
      BlackMicropoint.methods.resetDetailPopup.call(this);
      this.detailSelectedSpecificationId = null;
      this.detailSelectedRemarks = [];
      this.detailCustomRemark = "";
    },
    initDetailSpecificationState(goods) {
      if (!goods || !this.needsSpecification(goods)) {
        this.detailSelectedSpecificationId = null;
        this.detailSelectedRemarks = [];
        this.detailCustomRemark = "";
        return;
      }
      const specifications = goods.Specifications || [];
      this.detailSelectedSpecificationId = specifications.length
        ? String(specifications[0].Id)
        : "default";
      this.detailSelectedRemarks = [];
      this.detailCustomRemark = "";
    },
    toggleDetailRemark(remark) {
      if (!remark) return;
      const index = this.detailSelectedRemarks.indexOf(remark);
      if (index >= 0) {
        this.detailSelectedRemarks.splice(index, 1);
      } else {
        this.detailSelectedRemarks.push(remark);
      }
    },
    /**
     * 详情弹窗加入购物车：有规格菜品在弹窗内直接选规格 / 备注后加入，不再跳转规格弹窗。
     */
    addDetailGoods() {
      if (!this.detailGoods) return;
      const goods = this.detailGoods;
      const quantity = this.detailQuantity;

      if (this.isSetMeal(goods)) {
        this.closeDetail();
        this.$nextTick(() => this.openSetMeal(goods, null, quantity));
        return;
      }

      if (this.needsSpecification(goods)) {
        const specification = this.detailSpecificationOptions.find(
          (item) => item.id === this.detailSelectedSpecificationId
        );
        if (!specification) {
          this.$toast("当前规格不可用，请重新选择");
          return;
        }
        const remarkText = [...this.detailSelectedRemarks, this.detailCustomRemark]
          .filter(Boolean)
          .join("、");
        this.addCartItem(goods, specification, remarkText, quantity);
        this.closeDetail();
        return;
      }

      if (goods.Id || goods.GoodsCode) {
        this.closeDetail();
        this.addSimpleGoods(goods, quantity);
      }
    },
    /** 详情页展示可选备注（口味 / 做法 / 预设备注） */
    getDetailRemarkOptions(goods) {
      if (!goods) return [];
      const options = [
        ...(goods.TasteArray || []),
        ...(goods.MethodArray || []),
        ...(goods.GoodsRemarkArray || []),
      ]
        .map((item) => String(item || "").trim())
        .filter((item, index, array) => item && array.indexOf(item) === index);
      const staticRemark = String(goods.Remark || goods.remark || "").trim();
      if (staticRemark && !options.includes(staticRemark)) {
        options.unshift(staticRemark);
      }
      return options;
    },
    handleSubmit() {
      if (this.isSubmitting) return;
      if (!this.cartItems.length) {
        this.$toast("请先选择菜品");
        return;
      }
      this.isSubmitting = true;
      this.persistBlackCart();
      this.closeCart();
      this.$emit("submit-order", {
        items: this.cartItems.map((item) => ({ ...item })),
        totalCount: this.cartTotalCount,
        totalMoney: this.cartTotalMoney,
      });
      this.submitUnlockTimer = window.setTimeout(() => {
        this.isSubmitting = false;
        this.submitUnlockTimer = null;
      }, 500);
      this.$router.push({ name: getBlackCurrentConsumeRouteName() });
    },
  },
};
</script>

<style lang="scss" scoped>
/* 蓝湖菜单列表页（375 稿 ×2 = 750 标注） */
$bg: linear-gradient(to bottom, #252625, #333333);
$panel: #1a1a1a;
$gold: #ffc653;
$gold-btn: #f2cb78;
/* 按钮 / 选中态 / 招牌菜统一渐变 */
$gold-gradient: linear-gradient(135deg, #e6c260 0%, #fff1bc 50%, #e8c668 100%);
$text: #ffffff;
$soft: #cfcfcf;
$muted: #8f8f8f;
$chip: #2b2b2b;
$chip-on: #3a3a3a;
$cat-bg: #212120;
$cat-active: rgba(255, 189, 109, 0.25);
$cart-bg: #20201e;
$btn-text: #20201e;

$header-bg: #0a0a0a;
$header-bg-image: url("~@/assets/black-micropoint/v2-category-icons/header-bg.png");

.bv2-close-btn{
  margin-top: -10px;
  font-size: 60px !important;
}
.bv2-page {
  height: 100vh;
  height: 100dvh;
  min-height: 100vh;
  padding-bottom: calc(166px + env(safe-area-inset-bottom));
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  color: $text;
  background-color: $header-bg;
  color-scheme: dark;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
  -webkit-font-smoothing: antialiased;
}

.bv2-hero {
  background: transparent;
}

.bv2-brand {
  padding: calc(48px + env(safe-area-inset-top, 0px)) 24px 10px;
  border-bottom: 1px solid;
  border-image: linear-gradient(
      90deg,
      rgba(17, 16, 16, 1),
      rgba(255, 182, 110, 1),
      rgba(255, 172, 36, 1),
      rgba(28, 28, 27, 1)
    )
    1 1;
  background-color: transparent;
  background-image: $header-bg-image;
  background-repeat: no-repeat;
  background-position: center -80px;
  background-size: 100% auto;
  h1 {
    margin: 0;
    font-size: 40px;
    font-weight: 800;
    line-height: 56px;
    color: $text;
  }

  p {
    margin: 6px 0 42px;
    color: #bdbdbd;
    font-size: 18px;
    font-weight: 500;
    line-height: 30px;
  }
}

.bv2-order-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: 0;
  color: $text;
  background: transparent;
  font-size: 22px;
  font-weight: 500;
  line-height: 30px;
}

/* 折叠今日推荐：高约 100px（稿 50×2） */
/* 今日推荐：蓝湖 351×140 → 702×280 @750 */
.bv2-banner-container{
  padding: 0 24px;
}
.bv2-banner {
  margin-top: 20px;
  overflow: hidden;
  border-radius: 12px;
}

.bv2-banner-slide {
  position: relative;
  height: 280px;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.bv2-banner-mask {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  min-height: 120px;
  padding: 20px 6px;
  box-sizing: border-box;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.72) 100%
  );

  button {
    padding: 0;
    border: 0;
    color: #cfcbc8;
    background: transparent;
    font-size: 22px;
    font-weight: 500;
    text-shadow: 0 1px 4px rgba(0, 0, 0, 0.5);
  }
}

.bv2-banner-detail-link {
  display: inline-flex;
  align-items: center;
  appearance: none;
  -webkit-appearance: none;

  .bv2-detail-arrow {
    width: 16px;
    height: 16px;
    opacity: 0.5;
  }
}

.bv2-banner-copy {
  display: flex;
  flex-direction: column;

  strong {
    font-size: 30px;
    font-weight: 600;
    line-height: 44px;
  }

  span {
    margin-top: 4px;
    color: rgba(255, 255, 255, 0.72);
    font-size: 20px;
    font-weight: 500;
  }
}

.bv2-dots {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 12px;

  i {
    width: 20px;
    height: 4px;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.3);

    &.active {
      background: $gold;
    }
  }
}

/* 搜索 + 菜单区容器 */
/* 搜索：滚到顶后 sticky 固定（对齐 v1 .search-box） */
.bv2-search {
  position: sticky;
  top: 0;
  z-index: 10;
  padding: 20px 24px 16px;
  padding-bottom: 16px;
  margin: 0;
  background: #000000;
}

.bv2-search-inner {
  position: relative;
  height: 56px;
  padding: 0 28px;
  border-radius: 8px;
  border: 1px solid #ffd086;
  box-sizing: border-box;
  background: rgba(255, 255, 255, 0.3);
}

.bv2-search-wrap {
  position: relative;
  width: 100%;
  height: 100%;

  &.is-active .bv2-search-input {
    padding-right: 48px;
    text-align: left;
  }
}

.bv2-search-clear-btn {
  position: absolute;
  z-index: 3;
  right: 0;
  top: 50%;
  display: flex;
  width: 36px;
  height: 36px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  color: #fff;
  background: rgba(255, 255, 255, 0.16);
  font-size: 24px;
  line-height: 1;
  transform: translateY(-50%);
}

.bv2-search-decor {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 20px;
  color: rgba(255, 255, 255, 0.95);
  font-size: 24px;
  line-height: 1;
  white-space: nowrap;
  pointer-events: none;
  transform: translate(-50%, -50%);

}

.bv2-search-icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
}

.bv2-search-input {
  position: relative;
  z-index: 2;
  width: 100%;
  height: 56px;
  padding: 0;
  border: 0;
  box-sizing: border-box;
  color: $text;
  background: transparent;
  font-size: 24px;
  line-height: 56px;
  text-align: center;
  outline: 0;
  appearance: none;
  -webkit-appearance: none;

  &::-webkit-search-decoration,
  &::-webkit-search-cancel-button,
  &::-webkit-search-results-button,
  &::-webkit-search-results-decoration {
    display: none;
  }
}

.bv2-menu {
  display: flex;
  align-items: flex-start;
  min-height: calc(100vh - 166px - env(safe-area-inset-bottom));
  overflow: visible;
  background: transparent;
}

/* 侧栏 80×2=160，项高 90×2=180，选中半透明金 */
.bv2-cats {
  position: sticky;
  top: var(--bv2-search-h, 92px);
  align-self: flex-start;
  width: 160px;
  flex: 0 0 160px;
  max-height: calc(100vh - 166px - env(safe-area-inset-bottom));
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: $cat-bg;

  button {
    position: relative;
    display: flex;
    width: 100%;
    min-height: 180px;
    padding: 20px 8px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 0;
    color: $text;
    background: transparent;
    font-size: 24px;
    font-weight: 500;
    line-height: 30px;
    box-sizing: border-box;

    &.active {
      background: $cat-active;
      box-shadow: inset -4px 0 0 $gold;
    }

    i {
      position: absolute;
      top: 12px;
      right: 8px;
      min-width: 28px;
      height: 28px;
      padding: 0 6px;
      border-radius: 14px;
      color: $btn-text;
      background: $gold;
      font-size: 18px;
      font-style: normal;
      line-height: 28px;
      text-align: center;
    }
  }
}

.bv2-cat-icon {
  display: block;
  width: 66px;
  height: auto;
  margin-bottom: 8px;
  object-fit: contain;
}

.bv2-goods {
  flex: 1;
  min-width: 0;
  padding: 8px 24px 40px 26px;
  box-sizing: border-box;
  overflow: visible;
}

.bv2-goods-section {
  padding-bottom: 8px;
}

.bv2-cat-title {
  padding-top: 16px;
  padding-bottom: 22px;

  strong {
    color: $gold;
    font-size: 30px;
    font-weight: 600;
  }
}

.bv2-state {
  padding: 80px 20px;
  color: $muted;
  text-align: center;
  font-size: 26px;
}

/* 卡片：上图 180 + 下信息栏，总高约 264 */
.bv2-card {
  position: relative;
  margin-bottom: 16px;
  overflow: visible;
  border-radius: 8px;
  background: $panel;

  &.is-soldout {
    pointer-events: none;

    .bv2-card-info {
      opacity: 0.55;
    }

    .bv2-detail-link {
      opacity: 0.3;
    }
  }
}

.bv2-soldout-mask {
  position: absolute;
  z-index: 2;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  pointer-events: none;
}

/* 右上角裁切容器：丝带外缘与图片 top/right 平齐 */
.bv2-soldout-corner {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 3;
  width: 76px;
  height: 76px;
  overflow: hidden;
  pointer-events: none;
}

.bv2-soldout-tag {
  position: absolute;
  top: 16px;
  right: -32px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 28px;
  color: #333;
  background: linear-gradient(135deg, #e6c260 0%, #fff1bc 50%, #e8c668 100%);
  clip-path: polygon(8px 0, 100% 0, calc(100% - 8px) 100%, 0 100%);
  font-size: 18px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  transform: rotate(45deg);
}

.bv2-card-media {
  position: relative;
  height: 180px;
  overflow: visible;
}

.bv2-card-media-clip {
  position: relative;
  height: 100%;
  overflow: hidden;
  border-radius: 8px 8px 0 0;

  > img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.bv2-tag {
  position: absolute;
  left: -8px;
  top: 16px;
  z-index: 2;
  min-width: 84px;
  height: 36px;
  padding: 0 12px 0 16px;
  box-sizing: border-box;
  color: #262525;
  background: $gold-gradient;
  font-size: 22px;
  font-weight: 500;
  line-height: 36px;
  text-align: center;
}

.bv2-detail-link {
  position: absolute;
  right: 16px;
  bottom: 14px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: 0;
  appearance: none;
  -webkit-appearance: none;
  color: #fff;
  background: transparent;
  font-family: inherit;
  font-size: 22px; /* 750 稿标注，postcss 会转 vw，375 屏上约 11px */
  font-weight: 400;
  line-height: 1;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.55);
}

.bv2-detail-arrow {
  display: block;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.bv2-card-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 84px;
  padding: 18px 16px 18px;
  box-sizing: border-box;
}

.bv2-card-name {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  color: $text;
  font-size: 28px;
  font-weight: 600;
  line-height: 40px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.bv2-card-right {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 12px;
}

.bv2-card-price {
  margin: 0;
  color: $text;
  font-size: 30px;
  font-weight: 500;
  line-height: 40px;
  white-space: nowrap;

  small {
    margin-left: 2px;
    font-size: 20px;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.5);
  }
}

.bv2-spec-btn {
  flex-shrink: 0;
  min-width: 100px;
  height: 44px;
  padding: 0 16px;
  border: 0;
  border-radius: 6px;
  color: #333;
  background: $gold-gradient;
  font-size: 24px;

  &.block {
    width: 100%;
    min-width: 0;
    height: 72px;
    font-size: 28px;
  }

  &:disabled {
    color: #777;
    background: #3a3a3a;
  }
  &.bv2-submit-btn{
    color: #20201E !important;
    font-size: 36px !important;
    font-weight: 500 !important;
  }
}

.bv2-add-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  color: #333;
  background: $gold-gradient;
  font-size: 32px;
  font-weight: 500;
  line-height: 1;
}

.bv2-qty {
  display: flex;
  align-items: center;
  gap: 8px;

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    padding: 0;
    border: 0;
    border-radius: 6px;
    color: #333;
    background: $gold-gradient;
    font-size: 28px;
    line-height: 1;
  }

  span {
    min-width: 32px;
    color: $text;
    font-size: 26px;
    text-align: center;
  }
}

/* 底部购物车：蓝湖 375×83 → 750×166，左图标+文案 / 右去下单 */
.bv2-cart {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 20;
  gap: 20px;
  height: calc(140px + env(safe-area-inset-bottom));
  padding: 18px 24px calc(18px + env(safe-area-inset-bottom)) 32px;
  box-sizing: border-box;
  background: $cart-bg;
  // border-top: 1px solid rgba(255, 198, 83, 0.28);
  border: 1px solid;
border-image: linear-gradient(90deg, rgba(17, 16, 16, 1), rgba(255, 182, 110, 1), rgba(255, 172, 36, 1), rgba(28, 28, 27, 1)) 1 1;
}
.bv2-cart-container{
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
}

.bv2-cart-left {
  display: flex;
  flex: 1;
  align-items: center;
  min-width: 0;
  cursor: pointer;
}

.bv2-cart-icon {
  position: relative;
  flex-shrink: 0;
  width: 86px;
  height: 70px;
  margin-right: 24px;

  img {
    display: block;
    width: 86px;
    height: 70px;
    object-fit: contain;
  }

  i {
    position: absolute;
    top: -4px;
    right: -4px;
    min-width: 32px;
    height: 32px;
    padding: 0 6px;
    border-radius: 16px;
    color: $btn-text;
    background: $gold;
    font-size: 20px;
    font-style: normal;
    line-height: 32px;
    text-align: center;
  }
}

.bv2-cart-summary {
  flex: 1;
  min-width: 0;
  text-align: right;

  span {
    color: $text;
    font-size: 24px;
    font-weight: 500;
    line-height: 40px;
  }

  strong {
    display: block;
    color: $gold;
    font-size: 34px;
    font-weight: 700;
    line-height: 40px;
  }

  em {
    display: block;
    margin-top: 2px;
    color: $muted;
    font-size: 22px;
    font-style: normal;
  }
}

.bv2-submit {
  flex-shrink: 0;
  width: 178px;
  height: 78px;
  border: 0;
  border-radius: 10px;
  color: $btn-text;
  background: $gold-gradient;
  font-size: 32px;
  font-weight: 500;

  &:disabled {
    opacity: 0.55;
  }
}

.bv2-cart-edit {
  margin-top: 8px;
  padding: 0;
  border: 0;
  color: $gold;
  background: transparent;
  font-size: 22px;
}

.bv2-popup {
  background: #1a1a1a !important;
  color: $text;
  box-shadow: 0px 0px 4px 0px rgba(0,0,0,0.25);
}

.bv2-detail,
.bv2-cart-popup,
.bv2-spec,
.bv2-setmeal,
.bv2-setmeal-change {
  padding-bottom: env(safe-area-inset-bottom);
  // background: #1a1a1a;
  background: linear-gradient( 180deg, #3B3A3A 0%, #222120 100%)  !important;
}

.bv2-detail,
.bv2-cart-popup,
.bv2-spec,
.bv2-setmeal,
.bv2-setmeal-change {
  header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 40px 24px 40px;

    strong {
      font-size: 34px;
    }

    span {
      display: block;
      margin-top: 6px;
      color: $muted;
      font-size: 22px;
    }

    button {
      border: 0;
      color: $text;
      background: transparent;
      font-size: 40px;
      line-height: 1;
    }
  }

  footer:not(.bv2-spec-footer) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 16px 24px calc(16px + env(safe-area-inset-bottom));
    border-top: 1px solid #2a2a2a;
  }
}

.bv2-detail {
  img {
    display: block;
    width: 100%;
    height: 360px;
    object-fit: cover;
  }
}

.bv2-detail-body {
  padding: 20px 24px;

  > p {
    color: $soft;
    font-size: 24px;
    line-height: 36px;
  }
}

.bv2-detail-spec-body {
  margin-top: 20px;
  max-height: 40vh;
  overflow-y: auto;
  padding-right: 4px;

  .bv2-spec-group:last-child {
    margin-bottom: 0;
  }
}

.bv2-detail-remark {
  margin-top: 20px;

  h3 {
    margin: 0 0 8px;
    color: $text;
    font-size: 26px;
    font-weight: 500;
    line-height: 36px;
    &.opacity-50{
      color: rgba(255, 255, 255, 0.5);
      font-weight: normal;
    }
  }

  p {
    margin: 0;
    color: $soft;
    font-size: 24px;
    line-height: 36px;
  }
}

.bv2-detail-price {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
  &.pop-detail-price{
    strong {
    color: #ffffff;
    font-size: 48px;
    font-weight: normal;
  }
  small {
    color: rgba(255, 255, 255, 0.5);
    font-size: 28px;
    font-weight: normal;
  }
  }

  strong {
    color: $gold;
    font-size: 34px;
  }
}

.bv2-cart-list {
  max-height: 50vh;
  overflow-y: auto;
  padding: 0 24px;
}

.bv2-cart-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 0;
  border-bottom: 1px solid #2a2a2a;

  img {
    width: 96px;
    height: 96px;
    border-radius: 8px;
    object-fit: cover;
  }

  > div:nth-child(2) {
    flex: 1;
    min-width: 0;

    strong {
      display: block;
      font-size: 26px;
    }

    .bv2-cart-remark {
      margin-top: 6px;
      color: $soft;
      font-size: 22px;
      font-weight: 400;
      line-height: 30px;
    }

    p {
      margin-top: 8px;
      color: $gold;
      font-size: 24px;
    }
  }
}
.bv2-cart-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  color: $soft;
  font-size: 28px;
  font-weight: 500;

  strong {
    color: $gold;
    font-size: 30px;
  }
}

.bv2-cart-popup footer {
  .ghost,
  .bv2-spec-btn {
    flex: 1;
    min-width: 0;
    height: 78px;
    padding: 0 16px;
    border-radius: 8px;
    font-size: 32px;
    font-weight: 500;
  }
}

.bv2-spec-body {
  max-height: 55vh;
  overflow-y: auto;
  padding: 0 24px 12px;
}

.bv2-spec-group {
  margin-bottom: 40px;

  h3 {
    margin: 0 0 16px;
    color: $text;
    font-size: 26px;
    font-weight: 500;
    &.opacity-50{
      font-weight: normal;
      color: rgba(255, 255, 255, 0.5);
    }
  }

  textarea {
    display: block;
    width: 100%;
    min-height: 140px;
    padding: 18px;
    border: 0;
    border-radius: 10px;
    box-sizing: border-box;
    color: #333;
    background: rgba(255,255,255,0.5);
    font-size: 24px;
    resize: none;
    outline: 0;

    &::placeholder {
      color: #333;
    }
  }
}

.bv2-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;

  button {
    min-width: 110px;
    min-height: 44px;
    padding: 0 20px;
    border: 0;
    border-radius: 8px;
    color: #333;
    background:  rgba(255,255,255,0.5);
    font-size: 24px;

    &.active {
      color: #333;
      background: $gold-gradient;
      box-shadow: none;
    }
  }
}


/* 选规格弹窗底部：价格 + /份 + 加入购物车（蓝湖选规格弹窗） */
.bv2-spec-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 20px 24px calc(16px + env(safe-area-inset-bottom));
  border-top: 0;
  background: #20201e;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.25);
}

.bv2-spec-price {
  display: flex;
  flex-shrink: 0;
  align-items: baseline;

  strong {
    color: $text;
    font-size: 48px;
    font-weight: 500;
    line-height: 1;
  }

  small {
    margin-left: 4px;
    font-size: 28px;
    color: rgba(255, 255, 255, 0.5);
    font-weight: 500;
    line-height: 1;
  }
}

.bv2-spec-submit {
  flex: 1;
  max-width: 462px;
  height: 78px;
  padding: 0 20px;
  border: 0;
  border-radius: 6px;
  color: $btn-text;
  background: $gold-gradient;
  font-size: 36px;
  font-weight: 500;
  white-space: nowrap;

  &:disabled {
    color: #777;
    background: #3a3a3a;
  }
}

.bv2-setmeal-body,
.bv2-setmeal-change-list {
  max-height: 50vh;
  overflow-y: auto;
  padding: 0 24px;
}

.bv2-setmeal-item,
.bv2-setmeal-change-list > button {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 16px 0;
  border: 0;
  border-bottom: 1px solid #2a2a2a;
  color: $text;
  background: transparent;
  text-align: left;

  img {
    width: 88px;
    height: 88px;
    border-radius: 8px;
    object-fit: cover;
  }

  span {
    flex: 1;
    min-width: 0;

    strong {
      display: block;
      font-size: 26px;
    }

    small {
      display: block;
      margin-top: 6px;
      color: $muted;
      font-size: 22px;
    }
  }

  &.active {
    background: rgba(232, 193, 90, 0.08);
  }
}

.ghost {
  min-width: 178px;
  height: 78px;
  border: 1px solid #555;
  border-radius: 8px;
  color: $text;
  background: transparent;
  font-size: 32px;
}

.bv2-confirm {
  width: 80vw;
  background: #1f1f1f !important;

  section {
    padding: 36px 28px 28px;
    text-align: center;

    strong {
      font-size: 32px;
    }

    p {
      margin: 88px 0 88px;
      color: $soft;
      font-size: 26px;
    }

    > div {
      display: flex;
      gap: 30px;
      justify-content: center;

      .ghost,
      .bv2-confirm-btn {
        flex: 1;
        min-width: 0;
        height: 78px;
        padding: 0 16px;
        border-radius: 8px;
        font-size: 32px;
        font-weight: 500;
      }

      .bv2-confirm-btn {
        border: 0;
        color: $btn-text;
        background: $gold-gradient;
      }
    }
  }
}
</style>
